import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { axiomTheme, toneAlpha, toneColor, type Tone } from '../../styles/designTokens';

export interface DetailMetric {
  label: string;
  value: React.ReactNode;
  tone?: Tone;
  subtext?: React.ReactNode;
}

export interface DetailSection {
  title: string;
  eyebrow?: string;
  content: React.ReactNode;
}

interface ObjectDetailDrawerProps {
  open: boolean;
  title: string;
  subtitle?: React.ReactNode;
  status?: React.ReactNode;
  assetClass?: React.ReactNode;
  identifiers?: Array<{ label: string; value: React.ReactNode }>;
  metrics?: DetailMetric[];
  sections?: DetailSection[];
  actions?: React.ReactNode;
  onClose: () => void;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(2, 6, 23, 0.62);
  backdrop-filter: blur(4px);
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 81;
  width: min(760px, 100vw);
  overflow-y: auto;
  border-left: 1px solid ${axiomTheme.color.border.strong};
  background:
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.10), transparent 32%),
    ${axiomTheme.color.panel};
  box-shadow: ${axiomTheme.shadow.drawer};
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
  background: rgba(15, 23, 42, 0.92);
  backdrop-filter: blur(14px);
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
`;

const Title = styled.h2`
  margin: 0;
  color: ${axiomTheme.color.text.primary};
  font-size: 1.08rem;
  line-height: 1.22;
  font-weight: 850;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.div`
  margin-top: 0.35rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.78rem;
  line-height: 1.4;
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-top: 0.8rem;
`;

const Pill = styled.span<{ $tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${p => toneAlpha(p.$tone || 'slate', '38')};
  border-radius: ${axiomTheme.radius.pill};
  background: ${p => toneAlpha(p.$tone || 'slate', '18')};
  color: ${p => toneColor(p.$tone || 'slate')};
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  padding: 0.32rem 0.52rem;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  background: ${axiomTheme.surface.input};
  color: ${axiomTheme.color.text.muted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  width: 34px;

  &:hover {
    color: ${axiomTheme.color.text.primary};
    border-color: ${axiomTheme.color.border.strong};
  }
`;

const Body = styled.div`
  padding: 1rem 1.25rem 1.4rem;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div<{ $tone?: Tone }>`
  border: 1px solid ${p => toneAlpha(p.$tone || 'slate', '28')};
  border-radius: ${axiomTheme.radius.xl};
  background: rgba(15, 23, 42, 0.64);
  padding: 0.78rem;
`;

const MetricLabel = styled.div`
  color: ${axiomTheme.color.text.faint};
  font-size: 0.62rem;
  font-weight: 850;
  letter-spacing: 0.09em;
  margin-bottom: 0.42rem;
  text-transform: uppercase;
`;

const MetricValue = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-size: 1.02rem;
  font-weight: 850;
`;

const MetricSubtext = styled.div`
  color: ${axiomTheme.color.text.muted};
  font-size: 0.68rem;
  margin-top: 0.25rem;
`;

const IdentifierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Identifier = styled.div`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.lg};
  background: rgba(2, 6, 23, 0.28);
  padding: 0.5rem 0.58rem;
`;

const IdentifierLabel = styled.div`
  color: ${axiomTheme.color.text.faint};
  font-size: 0.58rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const IdentifierValue = styled.div`
  color: ${axiomTheme.color.text.secondary};
  font-size: 0.75rem;
  margin-top: 0.22rem;
  overflow-wrap: anywhere;
`;

const SectionCard = styled.section`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xl};
  background: rgba(2, 6, 23, 0.24);
  padding: 0.9rem;
  margin-top: 0.75rem;
`;

const SectionEyebrow = styled.div`
  color: ${axiomTheme.color.tone.cyan};
  font-size: 0.58rem;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 0.28rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.65rem;
  color: ${axiomTheme.color.text.primary};
  font-size: 0.84rem;
  font-weight: 850;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.9rem;
`;

export const ObjectDetailDrawer: React.FC<ObjectDetailDrawerProps> = ({
  open,
  title,
  subtitle,
  status,
  assetClass,
  identifiers = [],
  metrics = [],
  sections = [],
  actions,
  onClose,
}) => {
  if (!open) return null;

  return (
    <>
      <Backdrop onClick={onClose} />
      <Drawer>
        <Header>
          <HeaderTop>
            <div>
              <Title>{title}</Title>
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </div>
            <CloseButton onClick={onClose} aria-label="Close detail drawer">
              <X size={17} />
            </CloseButton>
          </HeaderTop>
          <HeaderMeta>
            {status && <Pill $tone="emerald">{status}</Pill>}
            {assetClass && <Pill $tone="cyan">{assetClass}</Pill>}
          </HeaderMeta>
          {identifiers.length > 0 && (
            <IdentifierGrid>
              {identifiers.map(item => (
                <Identifier key={item.label}>
                  <IdentifierLabel>{item.label}</IdentifierLabel>
                  <IdentifierValue>{item.value}</IdentifierValue>
                </Identifier>
              ))}
            </IdentifierGrid>
          )}
          {actions && <Actions>{actions}</Actions>}
        </Header>
        <Body>
          {metrics.length > 0 && (
            <MetricsGrid>
              {metrics.map(metric => (
                <MetricCard key={metric.label} $tone={metric.tone}>
                  <MetricLabel>{metric.label}</MetricLabel>
                  <MetricValue>{metric.value}</MetricValue>
                  {metric.subtext && <MetricSubtext>{metric.subtext}</MetricSubtext>}
                </MetricCard>
              ))}
            </MetricsGrid>
          )}
          {sections.map(section => (
            <SectionCard key={section.title}>
              {section.eyebrow && <SectionEyebrow>{section.eyebrow}</SectionEyebrow>}
              <SectionTitle>{section.title}</SectionTitle>
              {section.content}
            </SectionCard>
          ))}
        </Body>
      </Drawer>
    </>
  );
};

export default ObjectDetailDrawer;
